// ═══════════════════════════════════════════════════════════════
// Handoff — Dashboard
// Key metrics, incoming handoffs, active incidents
// Uses shared incidents from authStore
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, SimpleGrid, Group, Stack, Badge, Button,
  Loader, Center, Divider,
} from '@mantine/core';
import {
  IconArrowsExchange, IconCalendarEvent, IconChecklist,
  IconAlertTriangle, IconClock, IconSparkles,
} from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import AIBriefing from '../../components/handoff/AIBriefing';
import { primary, semantic } from '../../lib/colors';

export default function HandoffDashboard() {
  const navigate = useNavigate();
  const { incidents, activeIncident, loadIncidents } = useAuthStore();
  const { notes, actionItems, positions, isLoading, loadNotes, loadActionItems, loadPositions } = useHandoffStore();
  const [showAI, setShowAI] = useState(false);

  useEffect(() => {
    loadIncidents();
    loadNotes();
    loadActionItems();
    loadPositions();
  }, []);

  if (isLoading) {
    return <Center p="xl"><Loader /></Center>;
  }

  const activeIncidents = incidents.filter(e => e.status === 'active');
  const pendingNotes = notes.filter(n => n.status === 'submitted');
  const overdueActions = actionItems.filter(a =>
    a.status === 'pending' && a.deadline && new Date(a.deadline) < new Date()
  );
  const pendingActions = actionItems.filter(a => a.status === 'pending');

  // Incoming handoffs (all submitted)
  const incoming = notes.filter(n => n.status === 'submitted');

  return (
    <div>
      <Title order={3} mb="md" c="black">Handoff Dashboard</Title>

      {/* Key Metrics */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconCalendarEvent size={20} color="var(--blurple)" style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700}>{activeIncidents.length}</Text>
          <Text size="xs" c="dimmed">Active Incidents</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconArrowsExchange size={20} color={semantic.warning} style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c={semantic.warning}>{pendingNotes.length}</Text>
          <Text size="xs" c="dimmed">Pending Handoffs</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconAlertTriangle size={20} color={semantic.error} style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c={semantic.error}>{overdueActions.length}</Text>
          <Text size="xs" c="dimmed">Overdue Actions</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconChecklist size={20} color={semantic.info} style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c={semantic.info}>{pendingActions.length}</Text>
          <Text size="xs" c="dimmed">Pending Actions</Text>
        </Paper>
      </SimpleGrid>

      {/* AI Operational Briefing */}
      {activeIncident && (
        <Paper p="md" radius="sm" withBorder mb="md">
          <Group justify="space-between" mb={showAI ? 'md' : 0}>
            <Group gap="xs">
              <IconSparkles size={18} color={primary[700]} />
              <Text fw={600} size="sm">AI Operational Briefing</Text>
            </Group>
            <Button
              size="xs"
              variant={showAI ? 'light' : 'filled'}
              leftSection={<IconSparkles size={14} />}
              onClick={() => setShowAI(!showAI)}
            >
              {showAI ? 'Hide Briefing' : 'Generate AI Briefing'}
            </Button>
          </Group>
          {!showAI && (
            <Text size="sm" c="dimmed" mt="xs">
              Generate a comprehensive AI-powered operational briefing with hot issues, strengths & gaps, and priority actions from all handoff data.
            </Text>
          )}
          {showAI && (
            <>
              <Divider mb="md" />
              <AIBriefing
                incident={activeIncident}
                notes={notes}
                actionItems={actionItems}
                positions={positions}
              />
            </>
          )}
        </Paper>
      )}

      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        {/* Incoming Handoffs */}
        <Paper p="md" radius="sm" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">Incoming Handoffs</Text>
            <Button variant="subtle" size="xs" onClick={() => navigate('/handoff/notes')}>View All</Button>
          </Group>
          {incoming.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">No pending handoffs</Text>
          ) : (
            <Stack gap="xs">
              {incoming.slice(0, 5).map(n => {
                const inc = incidents.find(e => String(e.id) === String(n.eventId));
                return (
                  <Paper key={n.id} p="sm" radius="sm" withBorder
                    style={{ cursor: 'pointer' }} onClick={() => navigate(`/handoff/note/${n.id}`)}>
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>{n.positionName}</Text>
                        <Text size="xs" c="dimmed">{inc?.name} — {n.fromShift} → {n.toShift}</Text>
                      </div>
                      <Group gap="xs">
                        <Badge size="xs" color="yellow">Pending</Badge>
                        <Text size="xs" c="dimmed">{n.shiftDate}</Text>
                      </Group>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* Active Incidents */}
        <Paper p="md" radius="sm" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">Active Incidents</Text>
            <Button variant="subtle" size="xs" onClick={() => navigate('/handoff/events')}>View All</Button>
          </Group>
          {activeIncidents.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">No active incidents</Text>
          ) : (
            <Stack gap="xs">
              {activeIncidents.slice(0, 5).map(inc => {
                const noteCount = notes.filter(n => String(n.eventId) === String(inc.id)).length;
                const actionCount = actionItems.filter(a => String(a.eventId) === String(inc.id) && a.status === 'pending').length;
                return (
                  <Paper key={inc.id} p="sm" radius="sm" withBorder
                    style={{ cursor: 'pointer' }} onClick={() => navigate(`/handoff/event/${inc.id}`)}>
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>{inc.name}</Text>
                        <Text size="xs" c="dimmed" tt="capitalize">{inc.incidentType}</Text>
                      </div>
                      <Group gap="xs">
                        <Badge size="xs" variant="light" color="blurple">{noteCount} notes</Badge>
                        {actionCount > 0 && (
                          <Badge size="xs" variant="light" color="cyan">{actionCount} actions</Badge>
                        )}
                      </Group>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* Overdue Action Items */}
      {overdueActions.length > 0 && (
        <Paper p="md" radius="sm" withBorder style={{ border: '1px solid var(--color-error)' }}>
          <Group gap="xs" mb="sm">
            <IconClock size={16} color={semantic.error} />
            <Text fw={600} size="sm" c={semantic.error}>Overdue Action Items</Text>
          </Group>
          <Stack gap="xs">
            {overdueActions.slice(0, 5).map(a => {
              const inc = incidents.find(e => String(e.id) === String(a.eventId));
              return (
                <Group key={a.id} justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>{a.title}</Text>
                    <Text size="xs" c="dimmed">{inc?.name} — Assigned: {a.assignedToName || 'Unassigned'}</Text>
                  </div>
                  <Badge size="xs" color="red">
                    Due {new Date(a.deadline).toLocaleDateString()}
                  </Badge>
                </Group>
              );
            })}
          </Stack>
        </Paper>
      )}
    </div>
  );
}
